import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud, Camera, ImagePlus, X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { compressImage, formatBytes, MAX_EDGE } from '@/lib/imageCompress';
import { captureUploadError } from '@/lib/monitoring';

export const PHOTO_BUCKET = 'listing-photos';
/** Fallback only — the live limit comes from Admin → Settings (mkt_settings.listing_media). */
const MAX_FILES = 20;
const DEFAULT_MAX_MB = 10;
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];

interface Props {
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  /** Per-file size cap in megabytes (admin configurable). */
  maxMb?: number;
  /** Allowed MIME types (admin configurable). */
  accept?: string[];
}

const PhotoUploader: React.FC<Props> = ({ value, onChange, max = MAX_FILES, maxMb = DEFAULT_MAX_MB, accept = ACCEPTED }) => {

  const { t } = useI18n();
  const { profile } = useAuth();
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<{ from: number; to: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;
      setError('');
      setSaved(null);

      if (!profile) {
        setError(t('upload.errAuth'));
        return;
      }
      if (value.length + list.length > max) {
        setError(t('upload.errMax', { max }));
        return;
      }
      if (list.some((f) => !accept.includes(f.type.toLowerCase()) && !f.type.startsWith('image/'))) {
        setError(t('upload.errType'));
        return;
      }
      if (list.some((f) => f.size > maxMb * 1024 * 1024)) {
        setError(t('upload.errSize'));
        return;
      }


      setBusy(true);
      const uploaded: string[] = [];
      let bytesBefore = 0;
      let bytesAfter = 0;
      // PRIVACY: the public photo URL must never contain the owner's account
      // UUID. New uploads go under a random, non-identifying folder id that
      // cannot be reversed to a user, a profile or their other listings.
      // (Storage RLS only requires an authenticated caller — it does not key
      // off the folder name — so this is safe and needs no policy change.)
      const folder =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      for (const original of list) {
        // ------------------------------------------------------------------
        // DOWNSCALE IN THE BROWSER BEFORE UPLOAD.
        // Phone cameras produce 3-5 MB, 4000-5400px files. Those decode slowly
        // on mobile, and a slow decode is exactly when a blank image frame is
        // visible to a real user. compressImage() caps the long edge at
        // MAX_EDGE px, keeps the aspect ratio, re-encodes to WebP and aims for
        // ~500 KB. It NEVER throws: on any failure it returns the original
        // file so the homeowner can still post their photo.
        // ------------------------------------------------------------------
        const result = await compressImage(original);
        const file = result.file;
        bytesBefore += result.originalBytes;
        bytesAfter += result.bytes;

        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
        const key = `p/${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

        const { error: upErr } = await supabase.storage
          .from(PHOTO_BUCKET)
          .upload(key, file, { cacheControl: '3600', upsert: false, contentType: file.type || 'image/jpeg' });
        if (upErr) {
          captureUploadError(String(upErr.message || 'storage upload failed'), {
            bucket: PHOTO_BUCKET,
            stage: 'upload',
            status: (upErr as { statusCode?: string | number }).statusCode ?? '',
            bytes: file.size,
            type: file.type,
          });
          setError(t('upload.errFailed'));
          continue;
        }
        const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(key);
        if (data?.publicUrl) uploaded.push(data.publicUrl);
      }
      setBusy(false);
      if (bytesAfter && bytesBefore - bytesAfter > 50 * 1024) setSaved({ from: bytesBefore, to: bytesAfter });
      if (uploaded.length) onChange([...value, ...uploaded]);
    },
    [accept, max, maxMb, onChange, profile, t, value]

  );


  /** Reorder photos. Index 0 is always the cover image shown on the job card. */
  const move = (from: number, to: number) => {
    if (to < 0 || to >= value.length || from === to) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const removeAt = async (index: number) => {
    const url = value[index];
    onChange(value.filter((_, i) => i !== index));
    try {
      const marker = `/${PHOTO_BUCKET}/`;
      const idx = url.indexOf(marker);
      if (idx > -1) {
        const key = decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
        await supabase.storage.from(PHOTO_BUCKET).remove([key]);
      }
    } catch (_e) {
      /* thumbnail already removed from the form */
    }
  };


  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
        }}
        className={`rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
          dragging ? 'border-orange-400 bg-orange-50' : 'border-slate-300 bg-slate-50'
        }`}
      >
        <UploadCloud className={`mx-auto h-8 w-8 ${dragging ? 'text-orange-500' : 'text-slate-400'}`} />
        <p className="mt-2 hidden text-sm font-semibold text-slate-700 sm:block">{t('upload.drop')}</p>
        <p className="mt-1 hidden text-xs uppercase tracking-wide text-slate-400 sm:block">{t('upload.or')}</p>

        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || value.length >= max}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            <ImagePlus className="h-4 w-4" /> {t('upload.browse')}
          </button>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={busy || value.length >= max}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            <Camera className="h-4 w-4" /> {t('upload.camera')}
          </button>
        </div>

        {busy && (
          <p className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('upload.uploading')}
          </p>
        )}

        {/* Auto-optimisation notice. Purely informational — the resize already
            happened in the browser before the bytes left the device. */}
        {!busy && saved && (
          <p className="mt-3 text-xs font-semibold text-emerald-600">
            Photos optimized for fast loading — {formatBytes(saved.from)} → {formatBytes(saved.to)} (max {MAX_EDGE}px)
          </p>
        )}


        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {value.length > 0 && (
        <>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t('upload.count', { n: value.length, max })}
          </p>
          <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {value.map((url, i) => (
              <div key={url} className="group relative overflow-hidden rounded-xl border border-slate-200">
                <img src={url} alt="" className="h-24 w-full object-cover" loading="lazy" />
                {i === 0 && (
                  <span className="absolute left-1.5 top-1.5 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    {t('upload.cover')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label={t('upload.remove')}
                  title={t('upload.remove')}
                  className="absolute right-1.5 top-1.5 rounded-full bg-slate-900/80 p-1.5 text-white transition-colors hover:bg-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="absolute inset-x-0 bottom-0 flex justify-between bg-slate-900/70 px-1 py-1">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                    aria-label={t('upload.moveLeft')}
                    title={t('upload.moveLeft')}
                    className="rounded p-1 text-white disabled:opacity-30"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => move(i, 0)}
                      className="rounded px-1.5 text-[10px] font-bold uppercase text-white hover:text-orange-300"
                    >
                      {t('upload.makeCover')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={i === value.length - 1}
                    onClick={() => move(i, i + 1)}
                    aria-label={t('upload.moveRight')}
                    title={t('upload.moveRight')}
                    className="rounded p-1 text-white disabled:opacity-30"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
};

export default PhotoUploader;
